import { useUser, useSessionList, useClerk } from "@clerk/react";
import { useState } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  UserIcon,
  GoogleIcon,
  GithubIcon,
  ComputerIcon,
} from "@hugeicons/core-free-icons";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardAction,
} from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

/* ──────────────────────────── Profile ──────────────────────────── */
function ProfileSection() {
  const { user } = useUser();
  const clerk = useClerk();

  if (!user) return null;

  const initials = [user.firstName, user.lastName]
    .filter(Boolean)
    .map((n) => n![0])
    .join("");

  return (
    <Card className="bg-transparent">
      <CardHeader>
        <CardTitle>Profile</CardTitle>
        <CardDescription>Manage your personal information.</CardDescription>
        <CardAction>
          <Button
            variant="outline"
            size="sm"
            onClick={() => clerk.openUserProfile()}
          >
            Edit profile
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-4">
          <Avatar size="lg">
            <AvatarImage src={user.imageUrl} alt={user.fullName ?? ""} />
            <AvatarFallback>{initials}</AvatarFallback>
          </Avatar>
          <div className="flex flex-col gap-0.5">
            <span className="text-sm font-medium">{user.fullName}</span>
            <span className="text-muted-foreground text-sm">
              {user.primaryEmailAddress?.emailAddress}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/* ──────────────────── Connected Accounts ──────────────────────── */
const providerMeta: Record<string, { label: string; icon: typeof GoogleIcon }> =
  {
    google: { label: "Google", icon: GoogleIcon },
    github: { label: "GitHub", icon: GithubIcon },
  };

function ConnectedAccountsSection() {
  const { user } = useUser();

  if (!user) return null;

  const accounts = user.externalAccounts ?? [];

  return (
    <Card className="bg-transparent">
      <CardHeader>
        <CardTitle>Connected accounts</CardTitle>
        <CardDescription>Manage your linked sign-in providers.</CardDescription>
      </CardHeader>
      {accounts.length > 0 && (
        <CardContent className="space-y-3">
          {accounts.map((acct) => {
            const meta = providerMeta[acct.provider] ?? {
              label: acct.provider,
              icon: UserIcon,
            };
            return (
              <div key={acct.id} className="flex items-center gap-3">
                <div className="bg-muted flex size-8 items-center justify-center rounded-lg">
                  <HugeiconsIcon icon={meta.icon} className="size-4" />
                </div>
                <div className="flex flex-1 flex-col">
                  <span className="text-sm font-medium">{meta.label}</span>
                  <span className="text-muted-foreground text-xs">
                    {acct.emailAddress}
                  </span>
                </div>
                <span className="bg-emerald-500/10 text-emerald-500 rounded-md px-2 py-0.5 text-xs font-medium">
                  Connected
                </span>
              </div>
            );
          })}
        </CardContent>
      )}
      {accounts.length === 0 && (
        <CardContent>
          <p className="text-muted-foreground text-sm">
            No connected accounts.
          </p>
        </CardContent>
      )}
    </Card>
  );
}

/* ───────────────────── Active Sessions ─────────────────────────── */
function ActiveSessionsSection() {
  const { sessions } = useSessionList();

  return (
    <Card className="bg-transparent">
      <CardHeader>
        <CardTitle>Active sessions</CardTitle>
        <CardDescription>
          Devices where you are currently signed in.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {sessions && sessions.length > 0 ? (
          sessions.map((session) => {
            const lastActive = session.lastActiveAt;
            const isCurrent = session.status === "active";

            return (
              <div key={session.id} className="flex items-center gap-3">
                <div className="bg-muted flex size-8 items-center justify-center rounded-lg">
                  <HugeiconsIcon icon={ComputerIcon} className="size-4" />
                </div>
                <div className="flex flex-1 flex-col">
                  <span className="text-sm font-medium">Session</span>
                  <span className="text-muted-foreground text-xs">
                    Last active{" "}
                    {lastActive
                      ? new Date(lastActive).toLocaleDateString(undefined, {
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : "recently"}
                  </span>
                </div>
                {isCurrent ? (
                  <span className="bg-emerald-500/10 text-emerald-500 rounded-md px-2 py-0.5 text-xs font-medium">
                    Current
                  </span>
                ) : (
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={() => session.end()}
                  >
                    Revoke
                  </Button>
                )}
              </div>
            );
          })
        ) : (
          <p className="text-muted-foreground text-sm">No active sessions.</p>
        )}
      </CardContent>
    </Card>
  );
}

/* ──────────────────── Delete Account ───────────────────────────── */
function DeleteAccountSection() {
  const { user } = useUser();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (!user) return;
    setIsDeleting(true);
    try {
      await user.delete();
    } catch (err) {
      console.error("Failed to delete account:", err);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Card className="bg-transparent">
      <CardHeader>
        <CardTitle>Delete account</CardTitle>
        <CardDescription>
          Permanently delete your Rayloop account. This cannot be undone.
        </CardDescription>
        <CardAction className="self-center">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive">Delete account</Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                <AlertDialogDescription>
                  This action cannot be undone. This will permanently delete
                  your account and remove all of your data from our servers.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={isDeleting}
                >
                  {isDeleting ? "Deleting…" : "Delete account"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </CardAction>
      </CardHeader>
    </Card>
  );
}

/* ──────────────────── Main Export ──────────────────────────────── */
export function AccountSettings() {
  return (
    <div className="w-full space-y-6 p-8">
      <div>
        <h1 className="text-xl font-semibold">Account</h1>
        <p className="text-muted-foreground text-sm">
          Manage your account settings and preferences.
        </p>
      </div>
      <Separator />
      <ProfileSection />
      <ConnectedAccountsSection />
      <ActiveSessionsSection />
      <DeleteAccountSection />
    </div>
  );
}
